import AddIcon from '@mui/icons-material/Add';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import MenuIcon from '@mui/icons-material/Menu';
import PersonIcon from '@mui/icons-material/Person';
import RemoveIcon from '@mui/icons-material/Remove';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  InputBase,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { DASHBOARD_CODES, LEVEL_LABEL, SETTINGS_NAV, SIDEBAR_NAV, filterNav } from '@app/shared';
import type { NavItem } from '@app/shared';
import { AppLockup } from '../components/BrandLogo';
import { AssistantPanel } from '../features/assistant/AssistantPanel';
import { useAuth } from '../features/auth/AuthProvider';
import { ResetPasswordDialog } from '../features/auth/ResetPasswordDialog';
import { SessionTimeoutDialog } from '../features/auth/SessionTimeoutDialog';
import { ReleaseGate, useFeatureReleased, useReleased } from '../release';
import { CustomerSearch } from './CustomerSearch';
import { PageTitleProvider, useCurrentPageTitle } from './pageTitle';
import { useBrandLook, useShellColours } from './BrandTheme';
import { APP_NAME, tokens } from './theme';

const navLabel = (item: NavItem) => (item.code ? `${item.label} (${item.code})` : item.label);

const containsPath = (item: NavItem, pathname: string): boolean =>
  item.path
    ? pathname === item.path || pathname.startsWith(`${item.path}/`)
    : (item.children ?? []).some((c) => containsPath(c, pathname));

/** Among sibling leaves, only the longest matching path is active (/inventory vs /inventory/models). */
function activePath(items: NavItem[], pathname: string): string | undefined {
  let best: string | undefined;
  const walk = (list: NavItem[]) => {
    for (const item of list) {
      if (item.path && (pathname === item.path || pathname.startsWith(`${item.path}/`))) {
        if (!best || item.path.length > best.length) best = item.path;
      }
      if (item.children) walk(item.children);
    }
  };
  walk(items);
  return best;
}

/** Keeps the items whose label contains the text, and the groups that hold such items. */
function searchNav(items: NavItem[], text: string): NavItem[] {
  const needle = text.trim().toLowerCase();
  if (!needle) return items;
  const out: NavItem[] = [];
  for (const item of items) {
    const hit = navLabel(item).toLowerCase().includes(needle);
    if (item.children) {
      const children = hit ? item.children : searchNav(item.children, text);
      if (children.length) out.push({ ...item, children });
    } else if (hit) out.push(item);
  }
  return out;
}

/** The ring the reference draws before every menu entry. */
function Bullet({ color }: { color?: string }) {
  const colours = useShellColours();
  return (
    <Box
      aria-hidden
      sx={{
        width: 11,
        height: 11,
        mr: 0.75,
        mt: '3px',
        flexShrink: 0,
        borderRadius: '50%',
        border: `2px solid ${color ?? colours.sidebar.bullet}`,
      }}
    />
  );
}

interface BranchProps {
  item: NavItem;
  depth: number;
  active?: string;
  /** While the menu is being searched every matching group is shown open. */
  forceOpen: boolean;
}

function NavBranch({ item, depth, active, forceOpen }: BranchProps) {
  const { pathname } = useLocation();
  const released = useReleased();
  const { sidebar } = useShellColours();
  const [toggled, setToggled] = useState(() => containsPath(item, pathname));
  const open = forceOpen || toggled;

  if (item.children) {
    const top = depth === 0;
    return (
      <>
        <ListItemButton
          onClick={() => setToggled((o) => !o)}
          aria-expanded={open}
          sx={
            top
              ? {
                  minHeight: 44,
                  pl: 1.5,
                  gap: 0.75,
                  background: open ? sidebar.groupOpenBg : sidebar.groupBg,
                  borderBottom: '2px solid #fff',
                  '&:hover': { background: sidebar.groupOpenBg },
                }
              : {
                  minHeight: 34,
                  pl: 2.5,
                  alignItems: 'flex-start',
                  outline: open ? `2px solid ${sidebar.nestedBorder}` : undefined,
                  outlineOffset: -2,
                }
          }
        >
          {top ? (
            open ? (
              <ExpandMore sx={{ fontSize: 18 }} />
            ) : (
              <ChevronLeft sx={{ fontSize: 18 }} />
            )
          ) : (
            <Bullet color={open ? sidebar.nestedOpen : undefined} />
          )}
          <ListItemText
            primary={navLabel(item)}
            primaryTypographyProps={{
              fontSize: top ? 14 : 13.5,
              fontWeight: top || open ? 600 : 400,
              color: !top && open ? sidebar.nestedOpen : tokens.sidebarText,
            }}
            sx={{ my: 0 }}
          />
          {top ? null : open ? (
            <RemoveIcon sx={{ fontSize: 18 }} />
          ) : (
            <AddIcon sx={{ fontSize: 18 }} />
          )}
        </ListItemButton>
        <Collapse in={open} unmountOnExit>
          <List disablePadding>
            {item.children.map((child) => (
              <NavBranch
                key={`${child.label}-${child.code}`}
                item={child}
                depth={depth + 1}
                active={active}
                forceOpen={forceOpen}
              />
            ))}
          </List>
        </Collapse>
      </>
    );
  }

  const selected = item.path === active;
  return (
    <ListItemButton
      component={Link}
      to={item.path!}
      // Not released yet (release switches): shown in the menu, but it cannot be opened.
      disabled={!released(item.path)}
      selected={selected}
      aria-current={selected ? 'page' : undefined}
      sx={{
        minHeight: 34,
        pl: depth === 0 ? 1.5 : 1.5 + depth * 1.25,
        py: 0.5,
        alignItems: 'flex-start',
        ...(depth === 0
          ? { background: sidebar.groupBg, borderBottom: '2px solid #fff', minHeight: 44 }
          : {}),
        '&.Mui-selected': { fontWeight: 600 },
      }}
    >
      {depth === 0 ? <ChevronLeft sx={{ fontSize: 18, mr: 0.75 }} /> : <Bullet />}
      <ListItemText
        primary={navLabel(item)}
        primaryTypographyProps={{
          fontSize: depth === 0 ? 14 : 13.5,
          fontWeight: depth === 0 || selected ? 600 : 400,
          color: depth === 0 ? tokens.sidebarText : tokens.sidebarSubText,
          // The reference shows its menu entries in title case.
          sx: { textTransform: depth === 0 ? 'none' : 'capitalize' },
        }}
        sx={{ my: 0 }}
      />
      {item.badge ? (
        <Chip
          label={item.badge}
          size="small"
          color="primary"
          sx={{ height: 18, fontSize: 11, fontWeight: 700, borderRadius: 0.5, ml: 0.5 }}
        />
      ) : null}
    </ListItemButton>
  );
}

function Shell() {
  const { user, can, logout } = useAuth();
  const released = useReleased();
  const featureOn = useFeatureReleased();
  const [resetPassword, setResetPassword] = useState(false);
  const [assistant, setAssistant] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const pageTitle = useCurrentPageTitle();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [menuSearch, setMenuSearch] = useState('');
  const [userMenu, setUserMenu] = useState<HTMLElement | null>(null);
  const [settingsMenu, setSettingsMenu] = useState<HTMLElement | null>(null);

  const sidebar = useMemo(() => filterNav(SIDEBAR_NAV, can), [can]);
  const settings = useMemo(() => filterNav(SETTINGS_NAV, can), [can]);
  const shown = useMemo(() => searchNav(sidebar, menuSearch), [sidebar, menuSearch]);
  const active = useMemo(
    () =>
      activePath(
        [...sidebar, ...settings],
        // The wizard's pages belong to "New enrollments", not to the list that shares their prefix.
        /^\/enrollments\/[^/]+\/(step|done)/.test(pathname) ? '/enrollments/new' : pathname,
      ),
    [sidebar, settings, pathname],
  );
  const hasDashboard = DASHBOARD_CODES.some(can);
  const colours = useShellColours();
  const brandLook = useBrandLook();

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Box component="header" sx={{ display: 'flex', height: tokens.headerHeight, flexShrink: 0 }}>
        <Box
          sx={{
            width: tokens.sidebarWidth,
            flexShrink: 0,
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#fff',
          }}
        >
          {brandLook ? (
            <Box component={Link} to="/" sx={{ textDecoration: 'none', display: 'flex' }}>
              <AppLockup height={62} />
            </Box>
          ) : featureOn('brand-logo') ? (
            <Box component={Link} to="/" sx={{ textDecoration: 'none', display: 'flex' }}>
              <AppLockup height={62} />
            </Box>
          ) : (
            <Typography
              component={Link}
              to="/"
              sx={{ fontSize: 22, fontWeight: 700, color: colours.primary, textDecoration: 'none' }}
            >
              {APP_NAME}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            alignItems: 'flex-end',
            gap: 1.5,
            pr: 2,
            bgcolor: colours.topBar,
            color: '#fff',
            minWidth: 0,
          }}
        >
          {/* The white tab of the reference: menu button and the title of the page on screen. */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              alignSelf: 'flex-end',
              height: 50,
              pl: 1,
              pr: 5,
              bgcolor: colours.pageBg,
              color: '#000',
              clipPath: 'polygon(0 0, calc(100% - 34px) 0, 100% 100%, 0 100%)',
              borderTopLeftRadius: 4,
              minWidth: 0,
            }}
          >
            <Tooltip title="Toggle navigation">
              <IconButton
                size="small"
                color="inherit"
                onClick={() => setSidebarOpen((o) => !o)}
                aria-label="Toggle navigation"
              >
                <MenuIcon />
              </IconButton>
            </Tooltip>
            <Typography
              component="span"
              aria-label="Current page"
              sx={{
                fontSize: 15,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {pageTitle || (hasDashboard && pathname === '/' ? 'Dashboard' : APP_NAME)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1, height: '100%' }}>
            {released('/customers') ? <CustomerSearch /> : null}
            {user?.support ? (
              <Button
                color="inherit"
                variant="outlined"
                size="small"
                href="/hq"
                sx={{ whiteSpace: 'nowrap', ml: 1 }}
                aria-label="Company"
              >
                Company: {user.support.company} ▾
              </Button>
            ) : null}
            <Box sx={{ flexGrow: 1 }} />
            {featureOn('ai-assistant') ? (
              <>
                <Button
                  color="inherit"
                  variant="outlined"
                  size="small"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={() => setAssistant(true)}
                  sx={{ whiteSpace: 'nowrap', borderRadius: 4 }}
                >
                  AI Assistant
                </Button>
                <AssistantPanel open={assistant} onClose={() => setAssistant(false)} />
              </>
            ) : null}
            {featureOn('support-page') ? (
              <Tooltip title="Support">
                <IconButton color="inherit" component={Link} to="/support" aria-label="Support">
                  <HelpOutlineIcon />
                </IconButton>
              </Tooltip>
            ) : null}
            {featureOn('system-health') && (user?.level === 'admin' || can('PC965')) ? (
              <Tooltip title="System Health Status">
                <IconButton
                  color="inherit"
                  component={Link}
                  to="/system-health"
                  target="_blank"
                  rel="noopener"
                  aria-label="System Health Status"
                >
                  <MonitorHeartIcon />
                </IconButton>
              </Tooltip>
            ) : null}
            <ListItemButton
              onClick={(e) => setUserMenu(e.currentTarget)}
              sx={{ flexGrow: 0, borderRadius: 1, gap: 0.5 }}
              aria-haspopup="menu"
            >
              <PersonIcon />
              <Typography sx={{ fontSize: 15 }}>{user?.loginId}</Typography>
            </ListItemButton>
            {settings.length ? (
              <Tooltip title="Settings">
                <IconButton
                  color="inherit"
                  onClick={(e) => setSettingsMenu(e.currentTarget)}
                  aria-label="Settings"
                  aria-haspopup="menu"
                >
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
            ) : null}
          </Box>
        </Box>
      </Box>

      <Menu anchorEl={userMenu} open={Boolean(userMenu)} onClose={() => setUserMenu(null)}>
        <Box sx={{ px: 2, py: 1 }}>
          <Typography sx={{ fontWeight: 600 }}>{user?.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {user ? `${LEVEL_LABEL[user.level]} · ${user.groupName}` : ''}
          </Typography>
        </Box>
        <Divider />
        {featureOn('reset-password') ? (
          <MenuItem
            onClick={() => {
              setUserMenu(null);
              setResetPassword(true);
            }}
          >
            Reset Password
          </MenuItem>
        ) : null}
        <MenuItem
          onClick={() => {
            setUserMenu(null);
            void logout().then(() => navigate('/login'));
          }}
        >
          Log off
        </MenuItem>
      </Menu>
      {resetPassword ? <ResetPasswordDialog onClose={() => setResetPassword(false)} /> : null}

      <Menu
        anchorEl={settingsMenu}
        open={Boolean(settingsMenu)}
        onClose={() => setSettingsMenu(null)}
      >
        {settings.map((item) => (
          <MenuItem
            key={item.path}
            component={Link}
            to={item.path!}
            selected={item.path === active}
            disabled={!released(item.path)}
            onClick={() => setSettingsMenu(null)}
          >
            {navLabel(item)}
          </MenuItem>
        ))}
      </Menu>

      <Box sx={{ display: 'flex', flexGrow: 1, minHeight: 0 }}>
        {sidebarOpen ? (
          <Box
            sx={{
              width: tokens.sidebarWidth,
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              background: colours.sidebar.menuBg,
            }}
          >
            {/* The reference's menu search: type part of a name to find its entry. */}
            <Box sx={{ bgcolor: colours.topBar, p: 1.25 }}>
              <InputBase
                value={menuSearch}
                onChange={(e) => setMenuSearch(e.target.value)}
                placeholder="Search the menu"
                inputProps={{ 'aria-label': 'Search the menu' }}
                sx={{
                  width: '100%',
                  height: 36,
                  px: 2,
                  bgcolor: '#fff',
                  borderRadius: 5,
                  fontSize: 14,
                }}
              />
            </Box>
            <Box
              component="nav"
              aria-label="Main navigation"
              sx={{ overflowY: 'auto', flexGrow: 1 }}
            >
              <List disablePadding>
                {shown.map((item) => (
                  <NavBranch
                    key={`${item.label}-${item.code}`}
                    item={item}
                    depth={0}
                    active={active}
                    forceOpen={menuSearch.trim() !== ''}
                  />
                ))}
              </List>
              {shown.length === 0 ? (
                <Typography sx={{ p: 2, fontSize: 13 }}>No menu entry matches.</Typography>
              ) : null}
            </Box>
          </Box>
        ) : null}
        <Box
          sx={{
            flexGrow: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: colours.pageBg,
          }}
        >
          <Box component="main" sx={{ flexGrow: 1, p: 2, minWidth: 0 }}>
            {user?.support ? (
              <Alert
                severity="warning"
                sx={{ mb: 2 }}
                action={
                  <Button color="inherit" size="small" onClick={() => void logout()}>
                    End support session
                  </Button>
                }
              >
                212Ez support session in <b>{user.support.company}</b>, opened by {user.support.by}.
                Everything you change here is recorded under your login.
              </Alert>
            ) : null}
            <ReleaseGate>
              <Outlet />
            </ReleaseGate>
          </Box>
          <Box
            component="footer"
            sx={{
              px: 2,
              py: 1.5,
              borderTop: `1px solid ${tokens.border}`,
              color: tokens.cardTitle,
              bgcolor: '#fff',
            }}
          >
            <Typography variant="body2">{APP_NAME}</Typography>
          </Box>
        </Box>
      </Box>
      <SessionTimeoutDialog />
    </Box>
  );
}

/** Header, sidebar and content frame for every signed-in page. */
export function AppShell() {
  return (
    <PageTitleProvider>
      <Shell />
    </PageTitleProvider>
  );
}
